import { Injectable } from '@angular/core';
import { LocalStorageService } from '@vendasta/uikit';
import moment from 'moment';
import { Observable, ReplaySubject } from 'rxjs';

export enum ClientOrigin {
  MANAGE_ACCOUNTS_SALESPEOPLE = 'manage-accounts-salespeople',
  PIPELINE_PAGE_VIEW = 'pipeline-board-view',
  CURRENT_PARTNER_ID = 'current-partner-id',
  LAST_VIEWED_PIPELINE = 'last-viewed-pipeline',
  SSC_EXTENSION_OAUTH = 'ssc-gmail-extension-oauth',
}

const SEPARATOR = ':';
const EXPIRY = 'expiry';

export interface CacheEntry {
  origin: ClientOrigin;
  value: string;
}

export interface CacheGetResponse {
  origin: ClientOrigin;
  isCacheHit: boolean;
  value: any;
}

@Injectable({
  providedIn: 'root',
})
export class AppCacheService {
  private readonly cacheChange$$: ReplaySubject<ClientOrigin> = new ReplaySubject<ClientOrigin>(1);
  readonly cacheChange$: Observable<ClientOrigin> = this.cacheChange$$.asObservable();

  constructor(private readonly localStorage: LocalStorageService) {}

  private buildIndexedKey(origin: string): string {
    return `${origin}`;
  }

  private buildExpiryKey(origin: ClientOrigin): string {
    return `${origin}${SEPARATOR}${EXPIRY}`;
  }
  private getExpiryDate(origin: ClientOrigin): string {
    switch (origin) {
      case ClientOrigin.MANAGE_ACCOUNTS_SALESPEOPLE: {
        return moment().add(5, 'days').toISOString();
      }
      case ClientOrigin.PIPELINE_PAGE_VIEW: {
        return moment().add(1, 'years').toISOString();
      }
      case ClientOrigin.LAST_VIEWED_PIPELINE: {
        return moment().add(1, 'years').toISOString();
      }
      case ClientOrigin.SSC_EXTENSION_OAUTH: {
        return moment().add(3, 'minutes').toISOString();
      }
      default:
        return moment().add(5, 'days').toISOString();
    }
  }
  private updateCacheExpiryTime(origin: ClientOrigin): void {
    const expiryDate = this.getExpiryDate(origin);
    const expiryKey = this.buildExpiryKey(origin);
    this.localStorage.set(expiryKey, expiryDate);
  }

  private isCacheValid(origin: ClientOrigin): boolean {
    const expiryKey = this.buildExpiryKey(origin);
    const expiryDate = this.localStorage.get(expiryKey);
    return expiryDate ? moment(expiryDate).toDate() > moment().toDate() : false;
  }

  add(entry: CacheEntry): void {
    const indexedKey = this.buildIndexedKey(entry.origin);
    this.localStorage.set(indexedKey, entry.value);
    this.updateCacheExpiryTime(entry.origin);
    this.cacheChange$$.next(entry.origin);
  }
  get(origin: ClientOrigin): CacheGetResponse {
    const indexedKey = this.buildIndexedKey(origin);
    const rawValue = this.localStorage.get(indexedKey);
    return <CacheGetResponse>{
      origin: origin,
      isCacheHit: rawValue && this.isCacheValid(origin),
      value: rawValue && this.isCacheValid(origin) ? JSON.parse(rawValue) : null,
    };
  }
  remove(origin: ClientOrigin): void {
    const indexedKey = this.buildIndexedKey(origin);
    this.localStorage.remove(indexedKey);
    this.cacheChange$$.next(origin);
  }
}
